import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { CalendarBlank, CaretLeft, CaretRight, ArrowRight } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

// A Skyscanner-style date-range picker: pick a start day, then an end day, with
// the span highlighted as a connected band and rounded endpoints. Selecting a
// day before the current start simply moves the start, so an end can never be
// earlier than the start — the "start must not be after end" rule is enforced by
// construction, not by a post-hoc error. Future days are disabled (a contact
// can't be modified in the future). Values are `YYYY-MM-DD` strings.
//
// This is the repo's ONE date-range picker. It absorbed the analytics variant
// (which was a bare row of preset pills with no dismissal, no Escape and a
// custom panel that never closed on Apply) via the optional `presets` rail —
// pass `presets` + `activePreset` + `onPresetChange` for a preset-first control,
// omit them for a pure custom-range control.

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function parseDate(v?: string | null): Date | null {
  if (!v) return null
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}
function serialize(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}
function fmtShort(d: Date): string {
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`
}
function getMonthGrid(year: number, monthIdx: number) {
  const first = new Date(year, monthIdx, 1)
  const startDay = first.getDay()
  const start = new Date(year, monthIdx, 1 - startDay)
  const grid: { date: Date; inMonth: boolean }[] = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    grid.push({ date: d, inMonth: d.getMonth() === monthIdx })
  }
  return grid
}

export interface DateRangePreset {
  label: string
  /**
   * Opaque token the owner understands (Analytics passes `'7d' | '30d' | '90d'
   * | 'all'` straight to the API). The picker never computes a preset's dates —
   * it only reports which one was chosen.
   */
  value: string
}

export interface DateRangePickerProps {
  /** Applied start (YYYY-MM-DD), or '' / null for none */
  startDate?: string | null
  /** Applied end (YYYY-MM-DD), or '' / null for none */
  endDate?: string | null
  /** Called on Apply with the chosen range (either may be '' for open-ended). */
  onApply: (start: string, end: string) => void
  /** Called on Clear — removes the applied filter (or reverts to the owner's default preset). */
  onClear: () => void
  label?: string
  className?: string
  /**
   * Optional preset rail (§48 pills) down the left of the popover. Supplying
   * this is what makes the component usable for Analytics, which is
   * preset-first with a custom range as the escape hatch.
   */
  presets?: DateRangePreset[]
  /** The active preset's `value`, or null when a custom range is applied. */
  activePreset?: string | null
  /** Called when a preset pill is chosen. Required for the rail to be interactive. */
  onPresetChange?: (value: string) => void
}

const POPOVER_WIDTH = 320
const PRESET_RAIL_WIDTH = 132
const POPOVER_HEIGHT = 430

// §48 — a selected filter pill is a soft indigo tint, never a solid brand fill.
const PRESET_IDLE =
  'bg-card text-muted-foreground border-border/50 hover:bg-muted/40 hover:border-border/80 dark:bg-white/[0.02] dark:text-[#8a8f98] dark:border-white/[0.08] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14]'
const PRESET_ACTIVE =
  'bg-[#5e6ad2]/12 text-[#5e6ad2] border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40'

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onApply,
  onClear,
  label = 'Last modified',
  className,
  presets,
  activePreset = null,
  onPresetChange,
}) => {
  const appliedStart = parseDate(startDate)
  const appliedEnd = parseDate(endDate)
  const hasApplied = !!(appliedStart || appliedEnd)

  const hasPresets = !!presets?.length
  const activePresetLabel = hasPresets && !hasApplied
    ? presets!.find((p) => p.value === activePreset)?.label ?? null
    : null
  const popoverWidth = POPOVER_WIDTH + (hasPresets ? PRESET_RAIL_WIDTH : 0)

  const [open, setOpen] = useState(false)
  const [draftStart, setDraftStart] = useState<Date | null>(appliedStart)
  const [draftEnd, setDraftEnd] = useState<Date | null>(appliedEnd)
  const [hover, setHover] = useState<Date | null>(null)
  const [monthCursor, setMonthCursor] = useState<Date>(() => appliedStart ?? new Date())
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)

  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const today = useMemo(() => startOfDay(new Date()), [])

  const positionMenu = () => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const top = spaceBelow < POPOVER_HEIGHT && rect.top > POPOVER_HEIGHT
      ? rect.top - POPOVER_HEIGHT - 6
      : rect.bottom + 6
    let left = rect.right - popoverWidth
    if (left < 8) left = 8
    if (left + popoverWidth > window.innerWidth - 8) left = window.innerWidth - popoverWidth - 8
    setCoords({ top, left })
  }

  const openPicker = () => {
    // Seed the draft from the currently applied range each time we open.
    setDraftStart(appliedStart)
    setDraftEnd(appliedEnd)
    setHover(null)
    setMonthCursor(appliedStart ?? appliedEnd ?? new Date())
    positionMenu()
    setOpen(true)
  }
  const closePicker = () => setOpen(false)

  // Outside click / scroll / resize / Esc dismiss.
  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      closePicker()
    }
    const onScroll = (e: Event) => {
      const t = e.target as Node | null
      if (t && menuRef.current?.contains(t)) return
      closePicker()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePicker() }
    document.addEventListener('mousedown', onMouseDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', closePicker)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', closePicker)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleDayClick = (date: Date) => {
    const d = startOfDay(date)
    if (d > today) return // future days are disabled
    if (!draftStart || (draftStart && draftEnd)) {
      // Begin a fresh range.
      setDraftStart(d)
      setDraftEnd(null)
      return
    }
    // We have a start and no end.
    if (d < draftStart) {
      // Picking earlier than the start moves the start — end can never precede
      // start, so the "start > end" case is impossible.
      setDraftStart(d)
      return
    }
    setDraftEnd(d)
  }

  // The end used for highlighting: the committed end, or a live hover preview.
  const previewEnd =
    draftEnd ?? (draftStart && hover && hover >= draftStart ? hover : null)

  const apply = () => {
    if (!draftStart && !draftEnd) { onClear(); closePicker(); return }
    onApply(draftStart ? serialize(draftStart) : '', draftEnd ? serialize(draftEnd) : '')
    closePicker()
  }
  const clear = () => {
    setDraftStart(null)
    setDraftEnd(null)
    setHover(null)
    onClear()
    closePicker()
  }
  const canClear = !!(draftStart || draftEnd || hasApplied)

  const choosePreset = (value: string) => {
    // A preset supersedes any custom range — drop the draft so reopening the
    // popover doesn't show stale endpoints next to an active preset pill.
    setDraftStart(null)
    setDraftEnd(null)
    setHover(null)
    onPresetChange?.(value)
    closePicker()
  }

  const grid = getMonthGrid(monthCursor.getFullYear(), monthCursor.getMonth())
  const canGoNext = !(monthCursor.getFullYear() === today.getFullYear() && monthCursor.getMonth() === today.getMonth())

  const triggerText = appliedStart && appliedEnd
    ? `${fmtShort(appliedStart)} – ${fmtShort(appliedEnd)}`
    : appliedStart
      ? `After ${fmtShort(appliedStart)}`
      : appliedEnd
        ? `Before ${fmtShort(appliedEnd)}`
        : activePresetLabel ?? label

  return (
    <div className={cn('relative flex-shrink-0', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? closePicker() : openPicker())}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'group inline-flex h-9 items-center gap-2 rounded-md border px-3 transition-colors duration-150 max-w-[220px]',
          'bg-background border-border/60 text-foreground hover:border-border/80',
          'focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75',
          'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] dark:hover:border-white/[0.14]',
          (open || hasApplied) && 'border-[#7170ff]/55 dark:border-[#7170ff]/75',
        )}
        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
      >
        <CalendarBlank
          className={cn(
            'h-4 w-4 flex-shrink-0 transition-colors duration-150',
            open || hasApplied || activePresetLabel
              ? 'text-[#5e6ad2] dark:text-[#a5b4fc]'
              : 'text-muted-foreground dark:text-[#8a8f98]',
            'group-hover:text-[#5e6ad2] dark:group-hover:text-[#a5b4fc]',
          )}
          weight="regular"
        />
        <span className={cn('truncate', !hasApplied && !activePresetLabel && 'text-muted-foreground dark:text-[#8a8f98]')}>
          {triggerText}
        </span>
        {hasApplied && (
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#5e6ad2] dark:bg-[#a5b4fc]" aria-hidden />
        )}
      </button>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {open && coords && (
            <motion.div
              ref={menuRef}
              role="dialog"
              aria-label={`${label} date range`}
              initial={{ opacity: 0, scale: 0.97, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: -4 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              style={{ position: 'fixed', top: coords.top, left: coords.left, width: popoverWidth, zIndex: 200 }}
              className={cn(
                'rounded-lg border shadow-lg overflow-hidden',
                'bg-card border-border/60',
                'dark:bg-[#191a1b] dark:border-white/[0.08]',
                'dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]',
              )}
            >
              <div className="flex items-stretch">
              {/* Preset rail (§48 pills) — only when the owner supplies presets. */}
              {hasPresets && (
                <div
                  className="flex w-[132px] flex-shrink-0 flex-col gap-1.5 p-3 border-r border-border/50 dark:border-white/[0.06]"
                  role="group"
                  aria-label="Quick ranges"
                >
                  {presets!.map((p) => {
                    const isActive = !hasApplied && !draftStart && !draftEnd && activePreset === p.value
                    return (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => choosePreset(p.value)}
                        aria-pressed={isActive}
                        className={cn(
                          'w-full rounded-full border px-2.5 py-1.5 text-left transition-colors duration-150',
                          'focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75',
                          isActive ? PRESET_ACTIVE : PRESET_IDLE,
                        )}
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        {p.label}
                      </button>
                    )
                  })}
                </div>
              )}

              <div className="min-w-0 flex-1">
              {/* Range summary — Start -> End pills, Skyscanner-style */}
              <div className="flex items-stretch gap-2 px-3.5 pt-3.5 pb-3">
                <RangeCell heading="Start" value={draftStart ? fmtShort(draftStart) : 'Any'} active={!draftStart || !!draftEnd} />
                <div className="flex items-center text-muted-foreground/70 dark:text-[#62666d]">
                  <ArrowRight className="h-3.5 w-3.5" weight="bold" />
                </div>
                <RangeCell heading="End" value={draftEnd ? fmtShort(draftEnd) : 'Any'} active={!!draftStart && !draftEnd} />
              </div>

              {/* Month header */}
              <div className="flex items-center justify-between px-3.5 pb-1">
                <button
                  type="button"
                  onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}
                  aria-label="Previous month"
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150"
                >
                  <CaretLeft className="h-3.5 w-3.5" weight="bold" />
                </button>
                <span className="tabular-nums text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}>
                  {MONTHS[monthCursor.getMonth()]} {monthCursor.getFullYear()}
                </span>
                <button
                  type="button"
                  onClick={() => canGoNext && setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}
                  disabled={!canGoNext}
                  aria-label="Next month"
                  className={cn(
                    'p-1 rounded-md transition-colors duration-150',
                    canGoNext
                      ? 'text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]'
                      : 'text-muted-foreground/30 dark:text-[#3e3e44] cursor-not-allowed',
                  )}
                >
                  <CaretRight className="h-3.5 w-3.5" weight="bold" />
                </button>
              </div>

              {/* Weekday row */}
              <div className="grid grid-cols-7 px-2.5 pb-0.5">
                {WEEKDAYS.map((w, i) => (
                  <div key={i} className="text-center text-muted-foreground/70 dark:text-[#62666d] uppercase" style={{ fontSize: '9px', fontWeight: 590, letterSpacing: '0.06em', lineHeight: '20px' }}>
                    {w}
                  </div>
                ))}
              </div>

              {/* Day grid with connected range band */}
              <div className="grid grid-cols-7 px-2.5 pb-3" onMouseLeave={() => setHover(null)}>
                {grid.map(({ date, inMonth }, i) => {
                  const d = startOfDay(date)
                  const isFuture = d > today
                  const isToday = sameDay(d, today)
                  const isStart = !!draftStart && sameDay(d, draftStart)
                  const isEnd = !!previewEnd && sameDay(d, previewEnd)
                  const inRange = !!draftStart && !!previewEnd && d > draftStart && d < previewEnd
                  const isEndpoint = isStart || isEnd
                  const isMember = isEndpoint || inRange
                  const singleDay = isStart && isEnd

                  // Band segment: from the center outward so circular endpoints
                  // connect smoothly. Suppressed for a single-day selection.
                  let band = ''
                  if (isMember && !singleDay) {
                    if (isStart) band = 'left-1/2 right-0'
                    else if (isEnd) band = 'left-0 right-1/2'
                    else band = 'inset-x-0'
                  }

                  return (
                    <div key={i} className="relative h-9" onMouseEnter={() => !isFuture && setHover(d)}>
                      {band && (
                        <div className={cn('absolute inset-y-1 bg-[#5e6ad2]/[0.10] dark:bg-[#7170ff]/[0.16]', band)} aria-hidden />
                      )}
                      <button
                        type="button"
                        disabled={isFuture}
                        onClick={() => handleDayClick(d)}
                        className={cn(
                          'relative z-10 mx-auto flex h-9 w-9 items-center justify-center rounded-full tabular-nums transition-colors duration-100',
                          isEndpoint
                            ? 'bg-[#5e6ad2] text-white hover:bg-[#7170ff]'
                            : isFuture
                              ? 'text-muted-foreground/30 dark:text-[#3e3e44] cursor-not-allowed'
                              : inRange
                                ? 'text-foreground dark:text-[#f7f8f8]'
                                : isToday
                                  ? 'text-foreground dark:text-[#f7f8f8] ring-1 ring-inset ring-[#5e6ad2]/40 dark:ring-[#7170ff]/40 hover:bg-muted/40 dark:hover:bg-white/[0.05]'
                                  : inMonth
                                    ? 'text-foreground hover:bg-muted/40 dark:text-[#f7f8f8] dark:hover:bg-white/[0.05]'
                                    : 'text-muted-foreground/40 hover:bg-muted/30 dark:text-[#3e3e44] dark:hover:bg-white/[0.04]',
                        )}
                        style={{ fontSize: '11.5px', fontWeight: isEndpoint ? 590 : 510, letterSpacing: '-0.05px' }}
                      >
                        {date.getDate()}
                      </button>
                    </div>
                  )
                })}
              </div>

              </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 border-t border-border/50 dark:border-white/[0.06] bg-muted/20 dark:bg-white/[0.015]">
                <button
                  type="button"
                  onClick={clear}
                  disabled={!canClear}
                  className={cn(
                    'transition-colors duration-100',
                    canClear
                      ? 'text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8]'
                      : 'text-muted-foreground/40 dark:text-[#3e3e44] cursor-not-allowed',
                  )}
                  style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={apply}
                  disabled={!draftStart}
                  className={cn(
                    'h-8 px-3.5 rounded-md text-white shadow-sm transition-colors duration-150',
                    draftStart ? 'bg-[#5e6ad2] hover:bg-[#7170ff] active:bg-[#5058c0]' : 'bg-[#5e6ad2]/40 cursor-not-allowed',
                  )}
                  style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                >
                  Apply
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
}

function RangeCell({ heading, value, active }: { heading: string; value: string; active: boolean }) {
  return (
    <div
      className={cn(
        'flex-1 rounded-md border px-3 py-2 transition-colors duration-150',
        active
          ? 'border-[#5e6ad2]/50 bg-[#5e6ad2]/[0.06] dark:border-[#7170ff]/50 dark:bg-[#7170ff]/[0.10]'
          : 'border-border/60 dark:border-white/[0.08]',
      )}
    >
      <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground/80 dark:text-[#62666d]" style={{ fontWeight: 590 }}>
        {heading}
      </div>
      <div
        className={cn('tabular-nums mt-0.5', value === 'Any' ? 'text-muted-foreground/60 dark:text-[#62666d]' : 'text-foreground dark:text-[#f7f8f8]')}
        style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.1px' }}
      >
        {value}
      </div>
    </div>
  )
}

export default DateRangePicker
